import { baseApi } from './baseApi';
import type { AuthResponse } from '../types/auth';

export interface RestaurantSettings {
  id: string;
  name: string;
  description: string;
  address: string;
  phone: string;
  email: string;
  openTime: string; // TimeSpan as string "HH:mm:ss"
  closeTime: string;
  isOpen: boolean;
  taxRate: number;
  serviceCharge: number;
  currency: string;
  // Logo fields
  logoMimeType?: string;
  logoBase64?: string;
  logoFechaSubida?: string;
  logoNombreArchivo?: string;
  // Timezone (IANA format, e.g. America/Mexico_City)
  timeZone?: string;
}

export interface UpdateRestaurantSettingsRequest {
  name: string;
  description: string;
  address: string;
  phone: string;
  email: string;
  openTime: string;
  closeTime: string;
  isOpen: boolean;
  taxRate: number;
  serviceCharge: number;
  currency: string;
}

export interface Restaurant {
  id: string;
  name: string;
  organizationId: string;
  isActive: boolean;
  createdAt: string;
  /** El incluido en el plan base: no se le muestra "Cancelar". */
  isFirstRestaurant?: boolean;
}

export interface CreateRestaurantRequest {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
}

export interface UploadLogoRequest {
  logoBase64: string;
  logoMimeType: string;
  logoNombreArchivo?: string;
}

export interface RestaurantLogoDto {
  logoMimeType?: string;
  logoBase64?: string;
  logoFechaSubida?: string;
  logoNombreArchivo?: string;
}

export const restaurantsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRestaurantSettings: builder.query<RestaurantSettings, void>({
      query: () => '/restaurants/settings',
      providesTags: ['RestaurantSettings'],
    }),
    updateRestaurantSettings: builder.mutation<RestaurantSettings, UpdateRestaurantSettingsRequest>({
      query: (settings) => ({
        url: '/restaurants/settings',
        method: 'PUT',
        body: settings,
      }),
      invalidatesTags: ['RestaurantSettings'],
    }),
    uploadRestaurantLogo: builder.mutation<RestaurantLogoDto, UploadLogoRequest>({
      query: (logo) => ({
        url: '/restaurants/settings/logo',
        method: 'POST',
        body: logo,
      }),
      invalidatesTags: ['RestaurantSettings'],
    }),
    deleteRestaurantLogo: builder.mutation<void, void>({
      query: () => ({
        url: '/restaurants/settings/logo',
        method: 'DELETE',
      }),
      invalidatesTags: ['RestaurantSettings'],
    }),
    getRestaurants: builder.query<Restaurant[], void>({
      query: () => '/restaurants',
      providesTags: ['Restaurant'],
    }),
    // Solo los restaurantes activos donde el usuario tiene membresía (soporte ve
    // todos): alimenta el switcher del header.
    getAccessibleRestaurants: builder.query<Restaurant[], void>({
      query: () => '/restaurants/accessible',
      providesTags: ['Restaurant'],
    }),
    getRestaurant: builder.query<Restaurant, string>({
      query: (id) => `/restaurants/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Restaurant', id }],
    }),
    createRestaurant: builder.mutation<Restaurant, CreateRestaurantRequest>({
      query: (body) => ({
        url: '/restaurants',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Restaurant'],
    }),
    deleteRestaurant: builder.mutation<void, string>({
      query: (id) => ({
        url: `/restaurants/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Restaurant'],
    }),
    // Reemite el JWT de la sesión con otro restaurante. El que llama debe
    // despachar setCredentials con la respuesta y recargar la app.
    switchRestaurant: builder.mutation<AuthResponse, { restaurantId: string }>({
      query: (body) => ({
        url: '/auth/switch-restaurant',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetRestaurantSettingsQuery,
  useUpdateRestaurantSettingsMutation,
  useUploadRestaurantLogoMutation,
  useDeleteRestaurantLogoMutation,
  useGetRestaurantsQuery,
  useGetAccessibleRestaurantsQuery,
  useGetRestaurantQuery,
  useCreateRestaurantMutation,
  useDeleteRestaurantMutation,
  useSwitchRestaurantMutation,
} = restaurantsApi;
